import { differenceInCalendarDays, parseISO, startOfToday } from 'date-fns';
import { z } from 'zod';

const MIN_LEAD_DAYS = 3;
const MIN_DURATION_DAYS = 3;
const MAX_DURATION_DAYS = 10;
const MIN_CLIPPING_BUDGET = 500;
const MIN_INFLUENCER_BUDGET = 2000;

export const createCampaignSchema = z
  .object({
    campaignStartDate: z
      .string()
      .min(1, 'Start date is required')
      .pipe(z.iso.date('Must be a valid date (YYYY-MM-DD)')),
    campaignEndDate: z
      .string()
      .min(1, 'End date is required')
      .pipe(z.iso.date('Must be a valid date (YYYY-MM-DD)')),
    advertiser: z.string().min(1, 'Advertiser is required'),
    artist: z
      .string()
      .transform((v) => v.trim())
      .pipe(z.string().min(1, 'Artist is required')),
    track: z
      .string()
      .transform((v) => v.trim())
      .pipe(z.string().min(1, 'Track is required')),
    isrc: z
      .string()
      .transform((v) => v.trim().toUpperCase())
      .pipe(
        z
          .string()
          .min(1, 'ISRC is required')
          .regex(/^[A-Z]{2}[A-Z0-9]{3}\d{7}$/, 'Must be a valid ISRC format'),
      ),
    spotifyUrl: z
      .string()
      .min(1, 'Spotify URL is required')
      .pipe(z.url('Must be a valid URL')),
    tiktokLink: z
      .string()
      .transform((v) => v.trim())
      .pipe(z.union([z.literal(''), z.url('Must be a valid URL')])),
    instagramLink: z
      .string()
      .transform((v) => v.trim())
      .pipe(z.union([z.literal(''), z.url('Must be a valid URL')])),
    clippingEnabled: z.boolean(),
    clippingBudget: z.number().optional(),
    clippingPurchaseOrder: z.string().optional(),
    influencerEnabled: z.boolean(),
    influencerBudget: z.number().optional(),
    influencerPurchaseOrder: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.clippingEnabled) {
      if (!data.clippingBudget || data.clippingBudget < MIN_CLIPPING_BUDGET) {
        ctx.addIssue({
          code: 'custom',
          message: `Minimum spend is $${MIN_CLIPPING_BUDGET.toLocaleString()}`,
          path: ['clippingBudget'],
        });
      }

      if (!data.clippingPurchaseOrder?.trim()) {
        ctx.addIssue({
          code: 'custom',
          message: 'Purchase order is required',
          path: ['clippingPurchaseOrder'],
        });
      }
    }

    if (data.influencerEnabled) {
      if (
        !data.influencerBudget ||
        data.influencerBudget < MIN_INFLUENCER_BUDGET
      ) {
        ctx.addIssue({
          code: 'custom',
          message: `Minimum spend is $${MIN_INFLUENCER_BUDGET.toLocaleString()}`,
          path: ['influencerBudget'],
        });
      }

      if (!data.influencerPurchaseOrder?.trim()) {
        ctx.addIssue({
          code: 'custom',
          message: 'Purchase order is required',
          path: ['influencerPurchaseOrder'],
        });
      }
    }

    const hasChannel = data.clippingEnabled || data.influencerEnabled;

    if (!hasChannel) {
      ctx.addIssue({
        code: 'custom',
        message: 'At least one campaign channel must be enabled',
        path: ['clippingEnabled'],
      });

      return;
    }

    if (!data.campaignStartDate || !data.campaignEndDate) {
      return;
    }

    const today = startOfToday();
    const start = parseISO(data.campaignStartDate);
    const end = parseISO(data.campaignEndDate);

    const leadDays = differenceInCalendarDays(start, today);

    if (leadDays < MIN_LEAD_DAYS) {
      ctx.addIssue({
        code: 'custom',
        message: `Start date must be at least ${MIN_LEAD_DAYS} days from today`,
        path: ['campaignStartDate'],
      });
    }

    const duration = differenceInCalendarDays(end, start);

    if (duration < MIN_DURATION_DAYS) {
      ctx.addIssue({
        code: 'custom',
        message: `Campaign must be at least ${MIN_DURATION_DAYS} days long`,
        path: ['campaignEndDate'],
      });
    }

    if (duration > MAX_DURATION_DAYS) {
      ctx.addIssue({
        code: 'custom',
        message: `Campaign cannot exceed ${MAX_DURATION_DAYS} days`,
        path: ['campaignEndDate'],
      });
    }
  });

export type CreateCampaignFormValues = z.infer<typeof createCampaignSchema>;
